import React from 'react';
import QRcode from 'react-qr-code';
import NumberFormat from 'react-number-format';
import ReactDOMServer from 'react-dom/server';
import moment from 'utils/moment';
import { evaluateMath } from './mathEvaluator';
import ImgesScreen from './ImgesScreen';
import CustomGrid from './CustomGrid';
import countryCodes from './CountryCodes.json';
import CustomTable from './CustomTable';

let lastId = 0;

export const DATE_FIELD_TYPES = ['date', 'dateWithSeconds'];
export const DATE_TIME_FIELD_TYPES = ['datetime', 'datetimeWithSeconds'];
export const TIME_FIELD_TYPES = ['time', 'timeWithSeconds', 'only_time', 'only_time_with_seconds'];
export const DATE_SUBMIT_FIELD_TYPES = [...DATE_FIELD_TYPES, ...DATE_TIME_FIELD_TYPES];

const normalizeTimeFormat = (format = 'hh:mm A') => {
  if (!format) return 'hh:mm A';
  if (format.includes('ss')) return format;
  if (/[Aa]/.test(format)) return format.replace(/mm(?!:ss)/, 'mm:ss');
  return format.endsWith(':ss') ? format : `${format}:ss`;
};

export const getFieldUsesSeconds = (field, explicit = false) => Boolean(
  explicit
    || field?.sendWithSecond
    || field?.settings?.utcTimeAndSystemTime
    || field?.settings?.dateWithSeconds
    || field?.settings?.timeWithSeconds
    || field?.settings?.sendWithSecond
    || field?.dateWithSeconds
    || field?.timeWithSeconds
    || field?.utcTimeAndSystemTime
    || field?.type === 'dateWithSeconds'
    || field?.type === 'datetimeWithSeconds'
    || field?.type === 'timeWithSeconds'
    || field?.type === 'only_time_with_seconds'
);

export const getTimeFormat = (withSeconds = false) => {
  const storedFormat = localStorage.getItem('timeFormat') || 'hh:mm A';
  if (!withSeconds) {
    return storedFormat;
  }
  return localStorage.getItem('timeFormat_with_sec') || normalizeTimeFormat(storedFormat);
};

export const getDateTimeFormat = (withSeconds = false) => (
  `${localStorage.getItem('dateFormat') || 'MM/DD/YYYY'} ${getTimeFormat(withSeconds)}`
);

export const normalizeSubmittedDateValue = (field, value) => {
  if (value === undefined || value === null || value === '') {
    return value;
  }

  if (moment.isMoment?.(value)) {
    return value;
  }

  const useSeconds = getFieldUsesSeconds(field);
  const dateFormat = localStorage.getItem('dateFormat') || 'MM/DD/YYYY';
  const timeFormat = getTimeFormat(useSeconds);
  const timeFormatWithSeconds = getTimeFormat(true);
  const rawValue = typeof value === 'string' ? value.trim() : value;

  const parseWithFallback = (formats) => {
    const parsed = moment(rawValue, formats, true);
    if (parsed.isValid()) return parsed;
    return null;
  };

  const fieldType = field?.type;

  let parsed = null;
  if (DATE_FIELD_TYPES.includes(fieldType)) {
    parsed = parseWithFallback([
      dateFormat,
      `${dateFormat} ${timeFormat}`,
      `${dateFormat} ${timeFormatWithSeconds}`,
      moment.ISO_8601
    ]);
  } else if (DATE_TIME_FIELD_TYPES.includes(fieldType)) {
    parsed = parseWithFallback([
      `${dateFormat} ${timeFormat}`,
      `${dateFormat} ${timeFormatWithSeconds}`,
      dateFormat,
      moment.ISO_8601
    ]);
  } else if (TIME_FIELD_TYPES.includes(fieldType)) {
    parsed = parseWithFallback([
      timeFormat,
      timeFormatWithSeconds,
      'HH:mm',
      'HH:mm:ss',
      'hh:mm A',
      'hh:mm:ss A'
    ]);
  }

  if (parsed) return parsed;
  if (DATE_SUBMIT_FIELD_TYPES.includes(fieldType) || TIME_FIELD_TYPES.includes(fieldType)) {
    const lenientParsed = moment(rawValue);
    if (lenientParsed.isValid()) return lenientParsed;
    return value;
  }
  return value;
};

export const uniqueID = (prefix = 'id') => {
  lastId += 1;
  return `${prefix}-${lastId}`;
};

export const normalizeKeyForLookup = key => String(key || '').replace(/\s+/g, '_').toLowerCase();

export const getValueByKeyNormalized = (obj, key) => {
  if (!obj || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const normalizedKey = normalizeKeyForLookup(key);
  const matchedKey = Object.keys(obj).find(k => normalizeKeyForLookup(k) === normalizedKey);
  return matchedKey ? obj[matchedKey] : undefined;
};

export const formatInlineTableValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value?.map(v => formatInlineTableValue(v))?.filter(Boolean).join(', ');
  if (typeof value === 'object') {
    if (value?.label !== undefined) return formatInlineTableValue(value.label);
    if (value?.name !== undefined) return formatInlineTableValue(value.name);
    if (value?.value !== undefined) return formatInlineTableValue(value.value);
    if (value?.text !== undefined) return formatInlineTableValue(value.text);
    return Object.values(value)?.map(v => formatInlineTableValue(v))?.filter(Boolean).join(', ');
  }
  return String(value);
};

export const getInlineTableValueForKey = (sources = [], key) => {
  if (!key) return '';
  const resolved = sources
    .map(source => getValueByKeyNormalized(source, key))
    .find(v => v !== undefined && v !== null);
  return formatInlineTableValue(resolved);
};

export const getInlineTableValueForColumn = (sources = [], column) => {
  const columnId = column?.id || column?.value;
  return getInlineTableValueForKey(sources, columnId);
};

export const renderInfoForType = (
  field,
  value,
  info,
  OperationLog,
  badging,
  profileDetails,
  fromBadgeSignatory,
  sendWithSecond = false
) => {
  if (value === undefined || value=== null) {
    return '';
  }
  const useSeconds = getFieldUsesSeconds(field, sendWithSecond);
  const dateTimeFormat = getDateTimeFormat(useSeconds);
  const timeFormat = getTimeFormat(useSeconds);
  const hasLinkValidator = Array.isArray(field?.validators) && field?.validators?.some(
    validator => validator.type === 'link' || validator.label === 'Link' && field?.type === 'string'
  );

  if (hasLinkValidator && value) {
    const url = value.startsWith('http://') || value.startsWith('https://')
        ? value
        : `https://${value}`;

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#0066cc',
          textDecoration: 'underline',
          wordBreak: 'break-all'
        }}
      >
        {value}
      </a>
    );
  }
  let numberOut = value;
  switch (field.type) {
    case 'date':
      if (field?.widget?.type === 'year') {
        return value;
      }
      return moment(value).format(localStorage.getItem('dateFormat') || 'MM/DD/YYYY');
    case 'datetime':
    case 'dateWithSeconds':
    case 'datetimeWithSeconds':
      return value ? moment(value).format(dateTimeFormat) : '';
    case 'time':
    case 'only_time':
    case 'timeWithSeconds':
    case 'only_time_with_seconds':
      return value ? moment(value).format(timeFormat) : '';
    case 'string':
      return field?.widget?.type==='textfield' ?<p style={{ whiteSpace: 'pre-line' }}>{value}</p> : value;
    case 'dataSource':
      if (Array.isArray(value)) {
        return value.map(val => <i key={val.value}>{`${val.value} `}</i>);
      }
      if (value && typeof value === 'object') {
        return value?.value;
      }
      return value;
    case 'user_dropdown':
      if (!value) return '';
      return Array.isArray(value)
        ? value.map(item => (
            <i key={item.key || item}>{item.label || item}</i>
          ))
        : <i>{value.toString().replace(',', '')}</i>;
    case 'dataSourceMultiselect':
      return value?.map(val => (
        <i key={val}>{`${val} `}</i>
      ));
    case 'number':
    case 'currency':
    case 'Currency': {
      const isCurrency = (field?.inputType?.key?.toLowerCase?.() === 'currency'
        || field?.inputType?.toLowerCase?.() === 'currency'
        || field?.widget?.type?.toLowerCase?.() === 'currency'
        || field.type?.toLowerCase?.() === 'currency'
        || field?.validators?.key === 'Currency'
        || field?.validators?.type === 'currencyInput'
        || field?.validators?.some?.(v => v.type === 'currencyInput' || v.key === 'Currency')
      );
      if (isCurrency) {
        numberOut = <NumberFormat
          displayType="text"
          value={numberOut}
          thousandSeparator
          decimalScale={profileDetails?.portal_airport?.airport_settings?.decimal_precision || 2}
          fixedDecimalScale
          prefix={profileDetails?.portal_airport?.airport_settings?.currency || '$'}
          style={{ color: 'rgba(0,0,0,.87)', fontWeight: 'normal' }}
        />;
      }
      return numberOut;
    }
    case 'telephone':
      return value;
    case 'duration':
        return value;
    case 'calculation':
      return value;
    case 'cumulative_calculation':
      return value;
    case 'boolean':
      return value? 'Yes': 'No';
    case 'address':
      return value?.fulladdress;
    case 'suffix':
    case 'select': {
      let f = [];
      if (OperationLog) {
        f = info?.schema?.fields.find(e => e.id === field.id);
      } else if (badging || fromBadgeSignatory) {
        f = field;
      } else {
        f = info?.answer_schema?.fields.find(e => e.id === field.id);
      }
      return badging
        ? (f?.values?.find(e => e?.value === value.toString() || e?.key === value.toString())?.value)
        : f?.values?.find(e => e.key === value)?.value;
    }
    case 'multiselect': {
      let f = [];
      if (OperationLog) {
        f = info?.schema?.fields.find(e => e.id === field.id);
      } else if (fromBadgeSignatory) {
        f = field;
      } else {
        f = info?.answer_schema?.fields.find(e => e.id === field.id);
      }
      let vals;
      if (value) {
        value.forEach((val, i) => {
          vals = f?.values.find(e => e.key === val)
          && `${vals || ''}${f?.values.find(e => e.key === val).value}${((value.length - 1) !== i) ? ', ' : ''}`;
        });
      }
      return (
        vals
      );
    }

    case 'portal_user':
      return (
        Array.isArray(value)
        && value.map(user => (
          <i key={user.key}>
            {user?.label ? user?.label : user?.value}
          </i>
        ))
      );

    default:
      return null;
  }
};

export const actionForState = (action, state, message) => ({
  ...action,
  success: state === 'success',
  loading: state === 'pending',
  error: state === 'error',
  message: message || state
});

export const checkConditionalValue = (field, answers) => {
  let isValue = false;
  if (typeof answers[field?.conditional_field?.key] === 'string') {
    isValue = field?.conditional_values.filter(
      value => answers[field?.conditional_field?.key] === value.value
    ).length > 0;
  } else if (typeof answers[field?.conditional_field?.key] === 'object') {
    isValue = field?.conditional_values.filter(
      value => (
        answers[field?.conditional_field?.key].indexOf(value.value) !== -1
      ) === true
    ).length > 0;
  } else if (typeof answers[field?.conditional_field?.key] === 'boolean') {
    isValue = field?.conditional_values.filter(
      value => (
        answers[field?.conditional_field?.key].toString() === value?.value
      )
    ).length > 0;
  } else if (typeof answers[field?.conditional_field?.key] === 'number') {
    let operator = field?.conditional_values[0]?.operator?.name;
    isValue = field?.conditional_values.filter((value) => {
    if (value?.input) {
      switch (operator) {
        case '<':
          return answers[field?.conditional_field?.key] < value.input;
        case '>':
          return answers[field?.conditional_field?.key] > value.input;
        case '=':
          return answers[field?.conditional_field?.key] === Number(value.input);
        case '<=':
          return answers[field?.conditional_field?.key] <= value.input;
        case '>=':
          return answers[field?.conditional_field?.key] >= value.input;
        case '!=':
          return answers[field?.conditional_field?.key] !== Number(value.input);
        default:
          return false;
      }
    }
    return null;
    }).length > 0;
  }

  return isValue;
};

export const generateImges = photos => ReactDOMServer.renderToStaticMarkup(<ImgesScreen photos={photos} />);

export const showGridTable = (gridvalue, gridFields) => ReactDOMServer.renderToStaticMarkup(
  <CustomGrid gridValue={gridvalue} gridFields={gridFields} />
);

export const showCustomTable = (tableInfo, defaultStyle) => ReactDOMServer.renderToStaticMarkup(
  <CustomTable tableInfo={tableInfo} defaultStyle={defaultStyle} />
);

// qr code generate for html templates
export const generateQRcode = (id, val, defineSize) => ReactDOMServer.renderToStaticMarkup(
  generateUserQR(id, val, defineSize)
);

export const generateUserQR = (id, val, defineSize) => (
  <QRcode id={id || '123'} value={val || '...'} size={defineSize || 80} />
);

export const getAddress = (address) => {
  if (address) {
    return [
      address.street,
      address.city,
      address.state,
      address.zip
    ]
      .filter(Boolean)
      .join(', ');
  }
  return '';
};

export const getDatesFromRange = (range) => {
  let date = new Date();
  let date2 = new Date();
  date.setHours(0, 0, 0, 0);
  date2.setHours(23, 59, 99, 99);
  switch (range) {
    case 'Today':
      date = moment().startOf('day');
      date2 = moment().endOf('day');
      break;
    case 'Yesterday':
      date = moment().subtract(1, 'days').startOf('day');
      date2 = moment().subtract(1, 'days').endOf('day');
      break;
    case 'This Week':
      date = moment().startOf('week');
      date2 = moment().endOf('week');
      break;
    case 'Last Week':
      date = moment().subtract(1, 'weeks').startOf('week');
      date2 = moment().subtract(1, 'weeks').endOf('week');
      break;
    case 'This Month':
      date = moment().startOf('month');
      date2 = moment().endOf('month');
      break;
    case 'Last Month':
      date = moment().subtract(1, 'months').startOf('month');
      date2 = moment().subtract(1, 'months').endOf('month');
      break;
    case 'This Year':
      date = moment().startOf('year');
      date2 = moment().endOf('year');
      break;
    case 'Last 30 days':
      date = moment().subtract(30, 'days').startOf('days');
      date2 = moment().endOf('day');
      break;
    case 'All Data':
      date = new Date(0);
      date2 = moment().endOf('day');
      break;
    default:
      break;
  }
  return [
    date,
    date2
  ];
};

export const getShiftTypeByDate = (date, shiftTypes) => {
  if (!shiftTypes) return {};
  const hour = typeof date === 'object' ? date.hours() + 1 : new Date(date).getHours();
  const result = (shiftTypes && Array.isArray(shiftTypes) && shiftTypes?.filter(
    item => parseInt(item.start, 10) <= hour && parseInt(item.end, 10) > hour
  ).reduce((acc, shift) => shift, {})) || {};
  return Object.entries(result).length === 0 ? shiftTypes[shiftTypes.length - 1]: result;
};

export const getUserHasPermission = (permission, roles) => {
  const permissions = roles.map(e => e.permissions.map(p => p.codename)).reduce((p, n) => p.concat(n), []);
  return permissions.filter((a, b) => permissions.indexOf(a) === b).includes(permission);
};

export const calculateDuration = (first_date, second_date) => {
  if (first_date && second_date) {
    const first_date_wo_sec = new Date(first_date).setSeconds(0);
  const second_date_wo_sec = new Date(second_date).setSeconds(0);
  const date1 = new Date(first_date_wo_sec);
  const date2 = new Date(second_date_wo_sec);
  const duration = date2.getTime() - date1.getTime();
  return Math.floor(duration / (60000));
}
  return '-';
};

export const importAllImages = (r) => {
  const images = {};
  r.keys().map((item) => {
    images[item.replace('./', '')] = r(item);
    return true;
  });
  return images;
};

export const formattedCurrency = (countryCode, value, options = {}) => {
  const {
    metrics = undefined,
    currencySymbol = undefined,
    unit = undefined,
    unitDisplay = undefined,
    maximumSignificantDigits = undefined
  } = options;

  return new Intl.NumberFormat(countryCode, {
    style: metrics,
    currency: currencySymbol,
    unit,
    unitDisplay,
    maximumSignificantDigits
  }).format(value);
};

export const validFileName = (fileName) => {
  const validName =fileName.replace(/[^a-zA-Z0-9.]/g, '_');
  return validName;
};

export const allValidFileExtensions = `
  jpg, jpeg, gif, png, svg,
  doc, docx, pdf, odp,
  pps, ppt, pptx, ods,
  xlr, xls, xlsx, odt, csv,
  mov, mp4, mp3, json, webm
`;

export const profileExtensions = 'jpg, jpeg, png';

export const imageFileExtensions = 'jpg, jpeg, gif, png, svg';

export const docFileExtensions = 'doc, docx, pdf, odp, ods, odt';

export const excelFileExtensions = 'xlr, xls, xlsx, csv';

export const videoFileExtensions = 'mov, mp4, mp3, webm';

export const pptFileExtensions = 'pps, ppt, pptx, odp';

export const jsonFileExtensions = 'json';

export const zipFileExtensions = 'zip';

export const checkFilesValidation = (file, acceptFormat) => {
  const validFormats = acceptFormat?.split(',').map(i => i?.trim());
  const fileformat = file?.name?.split('.')?.pop();
  return validFormats?.includes(fileformat?.toLowerCase());
};

export const checkMultiFilesValidation = (files, acceptFormat) => {
  if (!files?.length) return false;
  if (!acceptFormat) return true;
  return Object.values(files)?.every(f => checkFilesValidation(f, acceptFormat));
};

export const getDefaultCountry = (code) => {
  const defaultCountry = countryCodes.find(country => country.Alpha2===code.toUpperCase() || country.Alpha3===code.toUpperCase());
  return { name: defaultCountry?.Country, iso2: defaultCountry?.Alpha2?.toLowerCase(), dialCode: defaultCountry?.Code, priority: 1 };
};

export const getDefaultCountryCode = (code) => {
  const defaultCountry = countryCodes.find(country => country.Alpha2===code.toUpperCase() || country.Alpha3===code.toUpperCase());
  return defaultCountry?.Code;
};

export const getTwoLetterCountryCode = (code) => {
  const twoCharCode = countryCodes.find(country => country.Alpha2===code.toUpperCase() || country.Alpha3===code.toUpperCase())?.Alpha2;
  return twoCharCode;
};

export const getThreeLetterCountryCode = (code) => {
  const threeCharCode = countryCodes.find(country => country.Alpha2===code.toUpperCase() || country.Alpha3===code.toUpperCase())?.Alpha3;
  return threeCharCode;
};

export const getCountryRegion = (code) => {
 let backendRegion = 'us';
 const region = countryCodes.find(country => country.Alpha2===code.toUpperCase() || country.Alpha3===code.toUpperCase())?.Region;
 if (code.toLowerCase() === 'au' || code.toLowerCase() === 'aus') {
  backendRegion = 'au';
} else if (code.toLowerCase() === 'in' || code.toLowerCase() === 'ind') {
  backendRegion = 'in';
} else if (region === 'eu') {
  backendRegion = 'eu';
}
 return backendRegion;
};

export const handleCalculation = (fields, answers, fromMultiGrid, allFields, multiFieldId,
  formApprovalSteps, exposeFieldsList, exposeFieldsData, fieldIdofMcd) => {
    let calculation = '';
    let formula = '';
    let valueArray = [];
    const getCalcFieldKey = field => field?.calc_field_id || field?.meta_data?.calc_field_id;
    if (fromMultiGrid) {
        const activeMultiFieldId = typeof multiFieldId === 'string' ? multiFieldId : multiFieldId?.id;
        const multiGridFields = allFields?.filter(e => (
          e.type === 'multi_field' && (!activeMultiFieldId || activeMultiFieldId === e.id) && (multiFieldId || fieldIdofMcd === e.id)
        ));
        if (multiGridFields) {
          multiGridFields.forEach((e) => {
            const multipleFields = Object.keys(e?.multiple_fields)?.map(i => ({ ...e?.multiple_fields[i], id: i }));
            const calcFields = multipleFields?.filter(field => field?.type?.key === 'calculation');
            const numberFields = multipleFields?.filter(field => field?.type?.key === 'number' || field?.type?.key === 'select'
              || field?.type?.key === 'calculation');
            if (calcFields?.length > 0) {
              let result = {};
              let resultArray = [];
              calcFields.forEach(async (field) => {
                formula = field.calculation;
                // eslint-disable-next-line no-useless-escape
                const formulaValues = formula?.replace(/\s+/g, '')?.split(/([()+*/-])/)?.filter(arr => arr!=='') || [];
                const formulaValueArray = numberFields?.filter(num => formulaValues?.filter(
                  arr => ((getCalcFieldKey(num) === arr) || (num.id === arr))
                )?.length > 0);
                if (formulaValueArray?.length > 0) {
                  resultArray = formulaValueArray?.map((num) => {
                    const calcFieldKey = getCalcFieldKey(num);
                    // Check if we have a live calculation (Eg: Number + Calculation) in 'result' from a previous step (dependent field).
                    // If not, fall back to the initial value from 'answers'.
                    const currentVal = result?.[calcFieldKey] !== undefined
                    ? result[calcFieldKey]
                    : answers[num.id];

                    const resultCodedValues = numberFields.find(val => getCalcFieldKey(val) === calcFieldKey)?.type?.key === 'select'
                    ? numberFields.find(val => getCalcFieldKey(val) === calcFieldKey)?.values?.find(v => v.key === answers[num.id])?.coded_value
                    : currentVal;
                    result[calcFieldKey || num.id] = resultCodedValues;
                    return result;
                  });
                }
                const answerValues = resultArray?.length > 0 ? resultArray?.[0] : answers;
                calculation = getFieldCalculation(formula, answerValues);
                // this line ensures that the calculated value is stored in the result object under the respective key
                result[getCalcFieldKey(field)] = calculation;
                valueArray.push({ value: calculation, id: field.id });
              });
            }
          });
        }
      } else {
        const calculationFields = fields?.filter(field => field.type === 'calculation') || [];
        let numberFields = fields?.filter(field => ((field.type === 'select' || field.type === 'number'
          || field.type === 'calculation') && field?.meta_data?.calc_field_id));
        if (calculationFields?.length > 0) {
          let result = {};
          let resultArray = [];
          let cleanAnswer = answers || {};
          const exposeData = exposeFieldsData || {};
          calculationFields.forEach((field) => {
            if (formApprovalSteps && field?.id.startsWith('d')) {
              numberFields = numberFields.filter(item => item.id.startsWith('d'));
            } else if (formApprovalSteps && field?.id.startsWith('ex')) {
              numberFields = exposeFieldsList?.filter(f => ((f.type === 'select' || f.type === 'number'
                || f.type === 'calculation'
              ) && f?.meta_data?.calc_field_id));
              Object.keys(answers).forEach((key) => {
                if (key.startsWith('ex-Forms-')) {
                  const formKey = key.replace('ex-Forms-', '');
                  if (formKey in exposeData) {
                    exposeData[formKey] = answers[key];
                  }
                }
              });
              cleanAnswer = exposeData;
            }
          formula = field.meta_data?.form_calculation?.calculation;
          // Wrap variables in parentheses to prevent errors when subtracting negative numbers (e.g., converting --2 to -(-2)).
          formula = formula.replace(/\b([A-Z]\d+)\b/g, '($1)');
          // eslint-disable-next-line no-useless-escape
          const formulaValues = formula?.replace(/\s+/g, '')?.split(/([()+*/-])/)?.filter(arr => arr!=='') || [];
          const formulaValueArray = numberFields?.filter(num => formulaValues?.filter(
            arr => ((num.meta_data?.calc_field_id === arr) || (num.id === arr))
          )?.length > 0);
          if (formulaValueArray?.length > 0) {
            resultArray = formulaValueArray?.map((num) => {
              let resultCodedValues;
              if (numberFields.find(val => val.id === num.id)?.type === 'select') {
                resultCodedValues = numberFields.find(val => val.id === num.id)?.values?.find(v => v.key === cleanAnswer[num.id])?.coded_value;
              } else {
                const calcId = num.meta_data?.calc_field_id;
                resultCodedValues = result[calcId] !== undefined ? result[calcId] : cleanAnswer[num.id];
              }
              result[num.meta_data?.calc_field_id || num.id] = resultCodedValues;
              return result;
               });
          }
          const answerValues = resultArray?.length > 0 ? resultArray?.[0] : cleanAnswer;
          calculation = getFieldCalculation(formula, answerValues);
          // this line ensures that the calculated value is stored in the result object under the respective key
          result[field?.meta_data?.calc_field_id] = calculation;
          valueArray.push({ value: calculation, id: field.id });
        });
        }
    }
      return valueArray;
  };

export const getFieldCalculation = (formula, answers) => {
    const noWsStr = formula?.replace(/\s+/g, '');
    const arrayValues = noWsStr && noWsStr.split(/([()+*/-])/)?.filter(arr => arr!=='') || [];
    let variable = '';
    const replaceArray = arrayValues?.map((arr) => {
        variable = answers && Object.keys(answers).find(a => a === arr);
        if (variable && answers[variable]?.toString()?.length && variable!==undefined) {
            return answers[variable]?.toString();
        }
      if (!Number.isNaN(Number(arr))) {
        return arr;
      }
      if (/^[^a-zA-Z0-9]+$/.test(arr)) {
        if (arr.match(/([()+*/-])/) || arr.match(/\d+[0-9]/)) {
          return arr;
        }
      }
        return '0';
    });
    const joinArray = replaceArray.join('');
    try {
        let result = evaluateMath(joinArray);
        return Number.isNaN(result) ? 0 : result;
    } catch (err) {
        return false;
    }
};


export const capitalizeFirstLetters = (string) => {
  if (!string) return string; // Handle empty or null strings
  return string
    .split(' ') // Split the string into an array of words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
    .join(' '); // Join the words back into a string
};

export const EMPTY_ARRAY = [];
/** *********************************************************
 *  Constants
 * ***************** */
export function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = deg2rad(lat2-lat1);
  const dLon = deg2rad(lon2-lon1);
  const a = Math.sin(dLat/2) * Math.sin(dLat/2)
    + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2))
    * Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const d = R * c;
  return d;
}
function deg2rad(deg) {
  return deg * (Math.PI/180);
}

export const getStripeKey = (airport_settings) => {
  const key = airport_settings?.stripe_key_type === 'individual' ? airport_settings?.stripe_public_key : process.env.REACT_APP_STRIPE_KEY;
  return key;
};

export const acceptFilesList = allValidFileExtensions.replace(/\s+/g, '').split(',').map(e => `.${e}`).join(',');

export const getField = (fields = [], fieldId = '') => fields?.find(eachField => eachField?.id=== fieldId);

export function getS3Region() {
  const regions = {
    'us': 'us-east-1',
    'cl': 'us-west-1',
    'in': 'ap-south-1',
    'inh': 'ap-south-2',
    'au': 'ap-southeast-2',
    'eu': 'eu-west-1',
    'uae': 'me-central-1',
    'local': 'us-east-1'
  };
  let current_region = localStorage.getItem('portalBackendId') ? regions[localStorage.getItem('portalBackendId')] : regions.us;
  return current_region;
}

export const formatCurrencyValue = (rawVal, decimalPrecision = 2) => {
  const num = Number(rawVal);
  if (!Number.isFinite(num)) return rawVal;
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimalPrecision,
    maximumFractionDigits: decimalPrecision
  }).format(num);
  const currencySymbol = localStorage.getItem('currency') || '$';
  return `${currencySymbol}${formatted}`;
};
